import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OfertaMAC, StatusOferta } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/Skeleton';
import {
  Plus, X, ChevronLeft, ChevronRight, Banknote, Pencil, Trash2,
} from 'lucide-react';
import clsx from 'clsx';

const STATUS_LABEL: Record<StatusOferta, string> = {
  ativo: 'Ativo',
  em_estruturacao: 'Em estruturação',
  encerrado: 'Encerrado',
};

const STATUS_STYLE: Record<StatusOferta, string> = {
  ativo: 'bg-green-50 border-green-300 text-green-700',
  em_estruturacao: 'bg-amber-50 border-amber-300 text-amber-700',
  encerrado: 'bg-slate-100 border-slate-300 text-slate-500',
};

export default function MacOfertas() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<OfertaMAC | null>(null);
  const [filterStatus, setFilterStatus] = useState<StatusOferta | 'todas'>('todas');
  const [mesAtual, setMesAtual] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const { data: ofertas, isLoading } = useQuery({
    queryKey: ['mac-ofertas'],
    queryFn: () => api.mac.ofertas.list().then((r) => r.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mac.ofertas.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mac-ofertas'] }),
  });

  const canEdit = user?.papel === 'Coordenação' || user?.papel === 'Diretoria';

  function handleDelete(o: OfertaMAC) {
    if (!confirm(`Excluir "${o.nome}"?`)) return;
    deleteMut.mutate(o.id);
  }

  const filtered = useMemo(() => {
    if (!ofertas) return [];
    let list = ofertas;
    if (filterStatus !== 'todas') {
      list = list.filter((o) => o.status === filterStatus);
    }
    return list;
  }, [ofertas, filterStatus]);

  // Ofertas no mês selecionado (por data reserva ou liquidação)
  const ofertasDoMes = useMemo(() => {
    return filtered.filter((o) => {
      const dr = o.dataReserva?.slice(0, 7);
      const dl = o.dataLiquidacao?.slice(0, 7);
      return dr === mesAtual || dl === mesAtual;
    });
  }, [filtered, mesAtual]);

  // Ofertas contínuas (sem data ou ativas indefinidamente)
  const ofertasContinuas = useMemo(() => {
    return filtered.filter((o) => o.status === 'ativo' && !o.dataReserva && !o.dataLiquidacao);
  }, [filtered]);

  const [year, month] = mesAtual.split('-').map(Number);
  const mesLabel = new Date(year, month - 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  function prevMes() {
    const d = new Date(year, month - 2);
    setMesAtual(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  function nextMes() {
    const d = new Date(year, month);
    setMesAtual(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Calendário de Ofertas"
        subtitle="Ofertas financeiras e ativações com prazo"
      />

      {/* Filters + actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1.5">
          {(['todas', 'ativo', 'em_estruturacao', 'encerrado'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStatus(s)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-[11px] font-medium border transition-colors',
                filterStatus === s ? 'bg-blue text-white border-blue' : 'bg-panel border-border text-muted hover:text-text',
              )}
            >
              {s === 'todas' ? 'Todas' : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue text-white text-xs font-medium hover:bg-blue/90"
          >
            <Plus className="w-3.5 h-3.5" /> Nova oferta
          </button>
        )}
      </div>

      <div className="flex gap-4">
        {/* Calendário mensal */}
        <div className="flex-1 bg-panel border border-border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <button type="button" onClick={prevMes} className="text-muted hover:text-text"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold capitalize">{mesLabel}</span>
            <button type="button" onClick={nextMes} className="text-muted hover:text-text"><ChevronRight className="w-4 h-4" /></button>
          </div>

          {isLoading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : ofertasDoMes.length === 0 ? (
            <div className="p-8 text-center text-muted text-xs">Nenhuma oferta neste mês.</div>
          ) : (
            <div className="divide-y divide-border">
              {ofertasDoMes.map((o) => (
                <OfertaRow key={o.id} oferta={o} />
              ))}
            </div>
          )}
        </div>

        {/* Sidebar — ofertas contínuas */}
        <div className="w-64 shrink-0 bg-panel border border-border rounded-lg">
          <div className="p-3 border-b border-border">
            <h3 className="text-xs font-bold text-muted uppercase tracking-wide">Ofertas contínuas</h3>
          </div>
          {ofertasContinuas.length === 0 ? (
            <div className="p-4 text-xs text-muted text-center">Nenhuma oferta contínua.</div>
          ) : (
            <div className="divide-y divide-border">
              {ofertasContinuas.map((o) => (
                <div key={o.id} className="px-3 py-2.5">
                  <div className="text-xs font-medium">{o.nome}</div>
                  {o.produto && <div className="text-[10px] text-muted">{o.produto}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabela completa */}
      <div className="bg-panel border border-border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-border">
              <th className="px-3 py-2 text-left font-semibold text-muted">Nome</th>
              <th className="px-3 py-2 text-left font-semibold text-muted">Área</th>
              <th className="px-3 py-2 text-left font-semibold text-muted">Produto</th>
              <th className="px-3 py-2 text-left font-semibold text-muted">Reserva</th>
              <th className="px-3 py-2 text-left font-semibold text-muted">Liquidação</th>
              <th className="px-3 py-2 text-left font-semibold text-muted">Status</th>
              {canEdit && <th className="px-3 py-2 w-16" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50/50 group">
                <td className="px-3 py-2 font-medium">{o.nome}</td>
                <td className="px-3 py-2 text-muted">{o.areaUnidade ?? '—'}</td>
                <td className="px-3 py-2 text-muted">{o.produto ?? '—'}</td>
                <td className="px-3 py-2 text-muted">{o.dataReserva ? fmtDate(o.dataReserva) : '—'}</td>
                <td className="px-3 py-2 text-muted">{o.dataLiquidacao ? fmtDate(o.dataLiquidacao) : '—'}</td>
                <td className="px-3 py-2">
                  <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded border', STATUS_STYLE[o.status])}>
                    {STATUS_LABEL[o.status]}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-3 py-2">
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => setEditing(o)} className="p-1 rounded hover:bg-slate-100 text-muted hover:text-blue" title="Editar">
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button type="button" onClick={() => handleDelete(o)} className="p-1 rounded hover:bg-red-50 text-muted hover:text-red-500" title="Excluir">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <FormModal onClose={() => setShowForm(false)} />}
      {editing && <FormModal initial={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function OfertaRow({ oferta: o }: { oferta: OfertaMAC }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <Banknote className="w-4 h-4 text-green-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{o.nome}</div>
        <div className="text-[10px] text-muted">
          {o.produto && `${o.produto} · `}
          {o.areaUnidade ?? ''}
          {o.rentabilidade && ` · ${o.rentabilidade}`}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {o.dataReserva && <span className="text-[10px] text-muted">Reserva: {fmtDate(o.dataReserva)}</span>}
        <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded border', STATUS_STYLE[o.status])}>
          {STATUS_LABEL[o.status]}
        </span>
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function FormModal({ onClose, initial }: { onClose: () => void; initial?: OfertaMAC }) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const [nome, setNome] = useState(initial?.nome ?? '');
  const [areaUnidade, setAreaUnidade] = useState(initial?.areaUnidade ?? '');
  const [produto, setProduto] = useState(initial?.produto ?? '');
  const [rentabilidade, setRentabilidade] = useState(initial?.rentabilidade ?? '');
  const [dataReserva, setDataReserva] = useState(initial?.dataReserva ?? '');
  const [dataLiquidacao, setDataLiquidacao] = useState(initial?.dataLiquidacao ?? '');
  const [status, setStatus] = useState<StatusOferta>(initial?.status ?? 'ativo');

  const payload = {
    nome,
    areaUnidade: areaUnidade || undefined,
    produto: produto || undefined,
    rentabilidade: rentabilidade || undefined,
    dataReserva: dataReserva || undefined,
    dataLiquidacao: dataLiquidacao || undefined,
    status,
  };

  const mut = useMutation({
    mutationFn: () => isEdit
      ? api.mac.ofertas.update(initial!.id, payload)
      : api.mac.ofertas.create(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mac-ofertas'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <div className="bg-panel rounded-xl border border-border shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-sm">{isEdit ? 'Editar Oferta' : 'Nova Oferta MAC'}</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Nome *">
            <input value={nome} onChange={(e) => setNome(e.target.value)} className="input-field" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Área/Unidade">
              <input value={areaUnidade} onChange={(e) => setAreaUnidade(e.target.value)} className="input-field" />
            </Field>
            <Field label="Produto">
              <input value={produto} onChange={(e) => setProduto(e.target.value)} className="input-field" />
            </Field>
          </div>
          <Field label="Rentabilidade">
            <input value={rentabilidade} onChange={(e) => setRentabilidade(e.target.value)} className="input-field" placeholder="Ex: CDI + 2%" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data reserva">
              <input type="date" value={dataReserva} onChange={(e) => setDataReserva(e.target.value)} className="input-field" />
            </Field>
            <Field label="Data liquidação">
              <input type="date" value={dataLiquidacao} onChange={(e) => setDataLiquidacao(e.target.value)} className="input-field" />
            </Field>
          </div>
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusOferta)} className="input-field">
              <option value="ativo">Ativo</option>
              <option value="em_estruturacao">Em estruturação</option>
              <option value="encerrado">Encerrado</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-panel-2">Cancelar</button>
          <button
            type="button"
            disabled={!nome.trim() || mut.isPending}
            onClick={() => mut.mutate()}
            className="px-3 py-1.5 text-xs rounded-md bg-blue text-white font-medium hover:bg-blue/90 disabled:opacity-50"
          >
            {mut.isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-muted mb-1 block">{label}</span>
      {children}
    </label>
  );
}
